import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Select, Heading, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Input, VStack,
} from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Custom cell renderer to display options with labels
const OptionsCellRenderer = (props) => {
  const options = props.value;
  const optionLabels = ['A', 'B', 'C', 'D'];

  if (!Array.isArray(options)) {
    return <span>No options available</span>;
  }

  return (
    <ul>
      {options.map((option, index) => (
        <li key={index} style={{ color: option.isCorrect ? 'green' : 'black' }}>
           {`${optionLabels[index]}. ${option.optionText}`}
        </li>
      ))}
    </ul>
  );
};

const ViewQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/questions`);
        const questionsData = response.data.$values || response.data;
        const formattedQuestions = questionsData.map(question => ({
          ...question,
          options: question.options.$values || question.options,
          categories: question.categories.$values || question.categories,
        }));
        setQuestions(formattedQuestions);
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
        setCategories(response.data.$values || response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchQuestions();
    fetchCategories();
  }, []);

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleEdit = (question) => {
    setEditingQuestion({
      ...question,
      options: question.options.$values || question.options,
    });
    onOpen();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/questions/${id}`);
        setQuestions(questions.filter((q) => q.questionId !== id));
      } catch (error) {
        console.error('Error deleting question:', error);
      }
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        questionId: editingQuestion.questionId,
        questionText: editingQuestion.questionText,
        categoryIds: editingQuestion.categoryIds || [],
        options: editingQuestion.options.map((option) => ({
          optionId: option.optionId,
          optionText: option.optionText,
          isCorrect: option.isCorrect,
        })),
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/questions/${editingQuestion.questionId}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*',
          },
        }
      );

      setQuestions(questions.map((q) => (q.questionId === editingQuestion.questionId ? editingQuestion : q)));
      setEditingQuestion(null);
      onClose();
    } catch (error) {
      console.error('Error updating question:', error.response ? error.response.data : error.message);
    }
  };

  const handleOptionTextChange = (e, index) => {
    const newOptions = [...editingQuestion.options];
    newOptions[index].optionText = e.target.value;
    setEditingQuestion({ ...editingQuestion, options: newOptions });
  };

  const handleOptionCorrectChange = (index) => {
    const newOptions = editingQuestion.options.map((option, idx) => ({
      ...option,
      isCorrect: idx === index,
    }));
    setEditingQuestion({ ...editingQuestion, options: newOptions });
  };
  const rowHeight = 180;

  const filteredQuestions = selectedCategory === 'All Categories'
    ? questions
    : questions.filter(q => q.categories.some(c => c.categoryName === selectedCategory));

  const columnDefs = [
    { headerName: 'S.No', valueGetter: 'node.rowIndex + 1', width: 70 },
    { headerName: 'Question', field: 'questionText', flex: 1 },
    {
      headerName: 'Options',
      field: 'options',
      cellRenderer: OptionsCellRenderer,
      flex: 1,
    },
    {
      headerName: 'Categories',
      field: 'categories',
      valueGetter: params => params.data.categories ? params.data.categories.map(c => c.categoryName).join(', ') : '',
      flex: 1,
    },
    {
      headerName: 'Actions',
      cellRenderer: (params) => (
        <div>
          <Button size="sm" colorScheme="blue" onClick={() => handleEdit(params.data)}>Edit</Button>
          <Button size="sm" colorScheme="red" ml={2} onClick={() => handleDelete(params.data.questionId)}>Delete</Button>
        </div>
      ),
      width: 150,
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
  };

  return (
    <Box p={8}>
      <Heading mb={6}>View Questions</Heading>
      <Select placeholder="Select Category" onChange={handleCategoryChange} mb={4}>
        <option value="All Categories">All Categories</option>
        {categories.map((category) => (
          <option key={category.categoryId} value={category.categoryName}>
            {category.categoryName}
          </option>
        ))}
      </Select>
      <div className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
        <AgGridReact
          rowData={filteredQuestions}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowHeight={rowHeight}
        />
      </div>

      {editingQuestion && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Question</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <Input
                  value={editingQuestion.questionText}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, questionText: e.target.value })}
                  placeholder="Question Text"
                />
                {editingQuestion.options.map((option, index) => (
                  <Box key={index} display="flex" alignItems="center">
                    <Input
                      value={option.optionText}
                      onChange={(e) => handleOptionTextChange(e, index)}
                      placeholder="Option Text"
                      mr={2}
                    />
                    <input
                      type="radio"
                      checked={option.isCorrect}
                      onChange={() => handleOptionCorrectChange(index)}
                    />
                  </Box>
                ))}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleSave}>
                Save
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default ViewQuestions;
